import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, X, ExternalLink } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { videoCategories } from "../../data/mockData";

const VideoCard = ({ video, onPlay }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="group relative cursor-pointer overflow-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onPlay(video)}
    >
      <div className="relative aspect-video overflow-hidden bg-[#141414]">
        <img
          src={video.thumbnail}
          alt={video.title}
          className={`w-full h-full object-cover transition-all duration-700 ${hovered ? 'scale-110 brightness-50' : 'scale-100 brightness-75'}`}
          loading="lazy"
        />
        {/* Play button overlay */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-16 h-16 rounded-full border-2 border-[#c8a55c] flex items-center justify-center bg-[#0a0a0a]/40 backdrop-blur-sm hover:bg-[#c8a55c]/20 transition-colors duration-300">
            <Play className="w-6 h-6 text-[#c8a55c] ml-1" />
          </div>
        </div>
        {/* Bottom border accent */}
        <div className={`absolute bottom-0 left-0 h-[2px] bg-[#c8a55c] transition-all duration-500 ${hovered ? 'w-full' : 'w-0'}`} />
      </div>
      <div className="py-4">
        <h4
          className="text-[#f5f0e8] text-lg group-hover:text-[#c8a55c] transition-colors duration-300"
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
        >
          {video.title}
        </h4>
      </div>
    </div>
  );
};

const VideoModal = ({ video, onClose }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      window.scrollTo(0, scrollY);
    };
  }, [handleKeyDown]);

  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-[#8a8070] hover:text-[#c8a55c] transition-colors duration-300 flex items-center gap-2"
        >
          <span
            className="text-sm tracking-[0.1em] uppercase"
            style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
          >
            Close
          </span>
          <X className="w-5 h-5" />
        </button>

        {/* Video iframe */}
        <div className="relative aspect-video bg-[#0a0a0a] border border-[#c8a55c]/10">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1&showinfo=0`}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Video info */}
        <div className="mt-4 flex items-center justify-between">
          <h3
            className="text-[#f5f0e8] text-2xl"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
          >
            {video.title}
          </h3>
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#8a8070] hover:text-[#c8a55c] transition-colors duration-300 flex items-center gap-2"
          >
            <span
              className="text-xs tracking-[0.1em] uppercase hidden sm:inline"
              style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
            >
              Watch on YouTube
            </span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

const WorkSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section
        id="work"
        ref={sectionRef}
        className="relative py-32 overflow-hidden"
        style={{ background: "#0a0a0a" }}
      >
        {/* Subtle top border */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#c8a55c]/30 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p
              className="text-[#c8a55c] text-sm tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600 }}
            >
              Filmography
            </p>
            <h2
              className="text-[#f5f0e8] text-5xl sm:text-6xl"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              Selected Works
            </h2>
          </div>

          {/* On Art & Creativity */}
          <div className={`mb-16 max-w-3xl border-l-2 border-[#c8a55c]/30 pl-8 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h3
              className="text-[#c8a55c] text-3xl mb-6"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
            >
              On Art & Creativity
            </h3>
            <div className="space-y-5">
              <p className="text-[#d0c8b8] text-base leading-[1.85]" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}>
                What is Art and Creativity I ask myself all the time. As a Director/Cameraman at a broadcast television station for over 40 years, every day was a new assignment, and I had to be "creative" no matter what. At times, it was challenging to come up with fresh ideas, but I had to get into that zone, a feeling, a place that would make me comfortable and calm.
              </p>
              <p className="text-[#d0c8b8] text-base leading-[1.85]" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}>
                Over time, I realized that creativity is not something that can be forced. It comes naturally when you're in the right mindset. It's like a switch that turns on when you're in a relaxed state, and your mind is open to new possibilities.
              </p>
              <p className="text-[#d0c8b8] text-base leading-[1.85]" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}>
                For me, it was about finding my happy place. I would take a few deep breaths, close my eyes, and visualize a peaceful and serene environment. It could be a beach, a forest, or a mountain top — whatever worked for me that day. Once I felt calm and focused, I could start to think about the task at hand.
              </p>
              <p className="text-[#d0c8b8] text-base leading-[1.85]" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}>
                The other thing that helped me was to keep an open mind. As a cameraman, I was always looking for unique angles and perspectives. I would try out different camera settings and experiment with lighting to create a unique look. Sometimes my colleagues would have an idea that I wouldn't have thought of, and it would end up being the perfect shot.
              </p>
              <p className="text-[#d0c8b8] text-base leading-[1.85]" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}>
                This is where I believe art and creativity intersect. Art is about expressing yourself through a medium, whether it's paint, music, or film. Creativity is about finding new ways to approach a problem or task. When you combine the two, you get something special — something that is uniquely yours.
              </p>
              <p className="text-[#d0c8b8] text-base leading-[1.85]" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}>
                Looking back on my career, I realize that every day was an opportunity to create something new and beautiful. It didn't matter if it was a simple news segment or a complex documentary — every project was a chance to flex my creative muscles and produce something that had never been seen before.
              </p>
              <p className="text-[#d0c8b8] text-base leading-[1.85]" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}>
                So, to answer the question of what is art and creativity, I believe it's a state of mind, a way of looking at the world with an open mind and a willingness to explore. It's not something that can be taught or learned but is instead a natural expression of who you are.
              </p>
              <p
                className="text-[#c8a55c] text-lg mt-4 italic"
                style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 400 }}
              >
                — Steve Sattler
              </p>
            </div>
          </div>

          {/* Tabbed Video Gallery */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <Tabs defaultValue="music-videos" className="w-full">
              <TabsList className="bg-transparent border-b border-[#1f1f1f] rounded-none h-auto p-0 mb-12 flex overflow-x-auto gap-0 scrollbar-hide">
                {videoCategories.map((cat) => (
                  <TabsTrigger
                    key={cat.id}
                    value={cat.id}
                    className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-[#c8a55c] data-[state=active]:bg-transparent data-[state=active]:text-[#c8a55c] text-[#8a8070] hover:text-[#b0a898] px-4 sm:px-6 py-4 text-xs sm:text-sm tracking-[0.12em] uppercase transition-all duration-300 data-[state=active]:shadow-none whitespace-nowrap flex-shrink-0"
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
                  >
                    {cat.label}
                    <span className="ml-2 text-xs opacity-50">({cat.videos.length})</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {videoCategories.map((cat) => (
                <TabsContent key={cat.id} value={cat.id} className="mt-0">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cat.videos.map((video) => (
                      <VideoCard
                        key={video.id}
                        video={video}
                        onPlay={setActiveVideo}
                      />
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {activeVideo && (
        <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </>
  );
};

export default WorkSection;
